import { useEffect, useState } from "react";
import { Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export const AppointmentEditor = ({ appt, services, onClose, onSaved }) => {
  const [form, setForm] = useState({
    service_id: appt.service_id,
    date: appt.date,
    time: appt.time,
    name: appt.name,
    phone: appt.phone,
    message: appt.message || "",
  });
  const [slots, setSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.service_id || !form.date) return;
    api.get("/availability", { params: { service_id: form.service_id, days: 40 } })
      .then((r) => {
        const day = r.data.days.find((d) => d.date === form.date);
        let times = day ? day.times : [];
        if (form.date === appt.date && appt.time && !times.includes(appt.time)) {
          times = [...times, appt.time].sort();
        }
        setSlots(times);
      })
      .catch(() => setSlots([]));
  }, [form.service_id, form.date]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/appointments/${appt.id}`, form);
      toast.success("Rendez-vous modifié");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Modification impossible"));
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-taupe/30 rounded-md px-3.5 py-2.5 text-sm text-espresso placeholder:text-espresso/40 focus:outline-none focus:ring-1 focus:ring-taupe transition-shadow duration-300";

  return (
    <div className="bg-cream border border-taupe/30 rounded-2xl p-5 space-y-3" data-testid={`appt-editor-${appt.id}`}>
      <div className="flex justify-between items-center">
        <h4 className="font-display text-lg text-espresso">Modifier le rendez-vous</h4>
        <button onClick={onClose} data-testid={`appt-editor-close-${appt.id}`} className="text-espresso/40 hover:text-espresso">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <select className={inputCls} data-testid={`appt-edit-service-${appt.id}`} value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })}>
          {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.price_label}</option>)}
        </select>
        <input type="date" className={inputCls} data-testid={`appt-edit-date-${appt.id}`} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })} />
        <select className={inputCls} data-testid={`appt-edit-time-${appt.id}`} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}>
          <option value="">Choisir l'horaire…</option>
          {slots.map((tm) => <option key={tm} value={tm}>{tm}</option>)}
        </select>
        <input className={inputCls} data-testid={`appt-edit-name-${appt.id}`} placeholder="Nom de la cliente" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input className={inputCls} data-testid={`appt-edit-phone-${appt.id}`} placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <input className={inputCls} data-testid={`appt-edit-message-${appt.id}`} placeholder="Message (optionnel)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </div>
      <div className="flex items-center gap-3">
        <button
          data-testid={`appt-edit-save-${appt.id}`}
          onClick={save}
          disabled={saving || !form.service_id || !form.date || !form.time || !form.name.trim() || !form.phone.trim()}
          className="rounded-full bg-taupe text-cream px-5 py-2.5 text-xs tracking-[0.1em] uppercase inline-flex items-center gap-2 hover:bg-taupe-dark transition-colors duration-300 disabled:opacity-40"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={1.5} />}
          Enregistrer
        </button>
        <p className="text-xs text-espresso/45">La cliente n'est pas notifiée automatiquement — prévenez-la via WhatsApp si l'horaire change.</p>
      </div>
    </div>
  );
};
